import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Plus, Calendar, MapPin, Users, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const emptyForm = { title: '', description: '', theme: '', date: '', time: '19:00', location: '', capacity: 20, price: 0, menu: '', image: '' };

export default function ChefEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchEvents = () => {
    API.get('/events').then(r => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetchEvents, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setOpen(true);
  };

  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      theme: ev.theme || '',
      date: ev.date || '',
      time: ev.time || '19:00',
      location: ev.location || '',
      capacity: ev.capacity || 20,
      price: ev.price || 0,
      menu: (ev.menu || []).join(', '),
      image: ev.image || '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      menu: form.menu.split(',').map(s => s.trim()).filter(Boolean),
      capacity: Number(form.capacity),
      price: Number(form.price),
    };
    try {
      if (editingId) {
        await API.put(`/events/${editingId}`, payload);
        toast.success('Event updated', { description: `"${form.title}" has been saved` });
      } else {
        await API.post('/events', payload);
        toast.success('Event created', { description: `"${form.title}" is live` });
      }
      setOpen(false);
      setForm({ ...emptyForm });
      setEditingId(null);
      fetchEvents();
    } catch {
      toast.error('Failed to save event');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await API.delete(`/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="fade-in space-y-6" data-testid="chef-events-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Events</h1>
        <Button onClick={openCreate} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold gap-1.5 btn-press" data-testid="create-event-btn">
          <Plus className="w-4 h-4" /> New Event
        </Button>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setEditingId(null); } setOpen(v); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{editingId ? 'Edit Event' : 'Create Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Title</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-title-input" />
            </div>
            <div>
              <Label className="text-sm">Theme</Label>
              <Input value={form.theme} onChange={e => setForm(f => ({ ...f, theme: e.target.value }))} placeholder="e.g. Italian, Japanese" className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-theme-input" />
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="mt-1 w-full rounded-lg border border-[#EBEBEB] px-3 py-2 text-sm" data-testid="event-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-date-input" />
              </div>
              <div>
                <Label className="text-sm">Time</Label>
                <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-time-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Location</Label>
              <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-location-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Capacity</Label>
                <Input type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-capacity-input" />
              </div>
              <div>
                <Label className="text-sm">Price per Seat ($)</Label>
                <Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-price-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Menu Items (comma-separated)</Label>
              <Input value={form.menu} onChange={e => setForm(f => ({ ...f, menu: e.target.value }))} placeholder="Appetizer, Main, Dessert" className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="event-menu-input" />
            </div>
            <Button onClick={handleSave} disabled={!form.title || !form.date} className="w-full bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold btn-press" data-testid="save-event-btn">
              {editingId ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {events.length === 0 ? (
        <div className="text-center py-16 text-[#717171]">No events created yet</div>
      ) : (
        <div className="space-y-4">
          {events.map(ev => (
            <div key={ev.id} className="bg-white rounded-xl border border-[#EBEBEB] p-5 flex items-start gap-5" data-testid={`chef-event-${ev.id}`}>
              {ev.image && <img src={ev.image} alt="" className="w-24 h-24 rounded-xl object-cover hidden sm:block" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-[#E05A47]/10 text-[#E05A47] text-xs font-semibold">{ev.theme}</span>
                </div>
                <h3 className="font-bold text-lg text-[#222] font-[Manrope]">{ev.title}</h3>
                <p className="text-sm text-[#717171] mt-0.5 line-clamp-1">{ev.description}</p>
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#717171]">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(ev.date).toLocaleDateString()} at {ev.time}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location}</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{ev.seats_booked || 0}/{ev.capacity} booked</span>
                  <span className="font-semibold text-[#222]">${ev.price}/seat</span>
                </div>
                {ev.menu?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ev.menu.map(m => <span key={m} className="px-2 py-0.5 bg-[#F7F7F7] rounded text-[10px] text-[#717171]">{m}</span>)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} className="text-[#717171] hover:text-[#222]" data-testid={`edit-event-${ev.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(ev.id, ev.title)} className="text-red-400 hover:text-red-600" data-testid={`delete-event-${ev.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
